import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flag, ExternalLink, Trash2, Check, XCircle } from 'lucide-react';
import { adminAPI } from '../../services/api';

const REASON_LABELS = {
  inappropriate: 'Inappropriate content',
  prohibited: 'Prohibited / recalled',
  counterfeit: 'Counterfeit / fake',
  misleading: 'Misleading',
  spam: 'Spam / scam',
  safety: 'Safety concern',
  other: 'Other',
};

const STATUS_COLORS = {
  pending: '#f5a623',
  reviewing: '#2196F3',
  resolved: '#0fa573',
  dismissed: '#767676',
};

const ReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const loadReports = async () => {
    try {
      setLoading(true);
      const params = filter ? { status: filter } : {};
      const res = await adminAPI.getReports(params);
      if (res.data.success) setReports(res.data.data);
    } catch (err) {
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const act = async (id, payload, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      setBusyId(id);
      await adminAPI.updateReport(id, payload);
      await loadReports();
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div>
      <h1 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Flag size={26} /> Reported Listings
      </h1>
      <p style={{ color: 'var(--textSecondary)', marginBottom: '24px' }}>
        Review listings reported by users for guideline violations. Check this daily.
      </p>

      {/* Filter */}
      <div style={{ marginBottom: '20px' }}>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '14px' }}
        >
          <option value="pending">Pending</option>
          <option value="reviewing">Reviewing</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
          <option value="">All</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--textSecondary)' }}>Loading reports…</div>
      ) : reports.length === 0 ? (
        <div style={{
          padding: '48px', textAlign: 'center', color: 'var(--textSecondary)',
          background: 'var(--cardBackground)', borderRadius: '8px', border: '1px solid var(--border)',
        }}>
          🎉 No {filter || ''} reports. Nothing to review.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {reports.map((r) => (
            <div key={r._id} style={{
              background: 'var(--cardBackground)',
              border: '1px solid var(--border)',
              borderLeft: `4px solid ${STATUS_COLORS[r.status] || '#767676'}`,
              borderRadius: '8px',
              padding: '16px',
            }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {/* Item thumb */}
                <div style={{ width: '72px', height: '72px', borderRadius: '8px', overflow: 'hidden', background: 'var(--secondary)', flexShrink: 0 }}>
                  {r.item?.images?.[0]?.thumbnail || r.item?.images?.[0]?.fullSize ? (
                    <img
                      src={r.item.images[0].thumbnail || r.item.images[0].fullSize}
                      alt={r.item?.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: 'var(--textSecondary)' }}>No image</div>
                  )}
                </div>

                {/* Details */}
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '15px' }}>{r.item?.title || '(item deleted)'}</strong>
                    <span style={{
                      background: STATUS_COLORS[r.status], color: 'white', fontSize: '11px',
                      fontWeight: 600, padding: '2px 8px', borderRadius: '10px', textTransform: 'uppercase',
                    }}>{r.status}</span>
                    {r.item?.status === 'deleted' && (
                      <span style={{ background: '#e74c3c', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>item removed</span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                    <strong>Reason:</strong> {REASON_LABELS[r.reason] || r.reason}
                  </div>
                  {r.description && (
                    <div style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '4px' }}>
                      "{r.description}"
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: 'var(--textSecondary)' }}>
                    Reported by {r.reporter?.username || 'unknown'} · Seller: {r.reportedUser?.username || 'unknown'} · {formatDate(r.createdAt)}
                  </div>
                  {r.adminNotes && (
                    <div style={{ fontSize: '12px', marginTop: '6px', fontStyle: 'italic' }}>Note: {r.adminNotes}</div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                {r.item?._id && (
                  <Link
                    to={`/items/${r.item._id}`}
                    target="_blank"
                    className="btn btn-sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid var(--border)' }}
                  >
                    <ExternalLink size={14} /> View Listing
                  </Link>
                )}
                {r.item?.status !== 'deleted' && (
                  <button
                    onClick={() => act(r._id, { removeItem: true, status: 'resolved' }, 'Remove this listing and resolve the report? The seller will be notified.')}
                    disabled={busyId === r._id}
                    className="btn btn-sm"
                    style={{ background: '#e74c3c', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Trash2 size={14} /> Remove Item
                  </button>
                )}
                {(r.status === 'pending') && (
                  <button
                    onClick={() => act(r._id, { status: 'reviewing' })}
                    disabled={busyId === r._id}
                    className="btn btn-sm"
                    style={{ background: '#2196F3', color: 'white' }}
                  >
                    Mark Reviewing
                  </button>
                )}
                {r.status !== 'resolved' && (
                  <button
                    onClick={() => act(r._id, { status: 'resolved' })}
                    disabled={busyId === r._id}
                    className="btn btn-sm"
                    style={{ background: '#0fa573', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Check size={14} /> Resolve
                  </button>
                )}
                {r.status !== 'dismissed' && (
                  <button
                    onClick={() => act(r._id, { status: 'dismissed' }, 'Dismiss this report (no violation)?')}
                    disabled={busyId === r._id}
                    className="btn btn-sm"
                    style={{ border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <XCircle size={14} /> Dismiss
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
